<template>
  <v-container fluid class="rv-report-page">
    <!-- Header Card -->
    <div class="rv-report-header">
      <div class="rv-report-header__top">
        <div class="rv-report-header__info">
          <h1 class="rv-report-header__title">Report Analysis</h1>
        </div>
      </div>
    </div>

    <AError :api="apiGenerateReport" />
    <AError :api="apiDownloadFinanceReport" />

    <!-- Tabs Navigation -->
    <div class="rv-report-tabs-wrapper">
      <v-tabs v-model="tab" background-color="transparent" class="rv-report-tabs" height="48">
        <!-- Duration Analysis tab hidden — resource heavy -->
        <v-tab :key="1" :href="`#tab-1`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-chart-timeline-variant-shimmer</v-icon>
          Duration Analysis
        </v-tab>
        <v-tab :key="2" :href="`#tab-2`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-note-search</v-icon>
          Generate Report
        </v-tab>
        <v-tab :key="3" :href="`#tab-3`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-file-chart</v-icon>
          Finance Report
        </v-tab>
        <v-tab :key="4" :href="`#tab-4`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-microsoft-excel</v-icon>
          HRDC Event Details
        </v-tab>
      </v-tabs>
    </div>

    <!-- Tab Content -->
    <div class="rv-report-content">
      <v-tabs-items v-model="tab">
        <!-- Duration Analysis tab content hidden — resource heavy -->
        <v-tab-item :key="1" :value="`tab-1`">
          <v-card flat class="rv-report-content-card">
            <duration-analysis-report
              :params="params"
            ></duration-analysis-report>
          </v-card>
        </v-tab-item>
        <v-tab-item :key="2" :value="`tab-2`">
          <v-card flat class="rv-report-content-card">
            <generate-report
              :generateReportData="generateReportData"
              :generateReportError="generateReportError"
              :showGenerateReport="showGenerateReport"
              :notReadyReportData="notReadyReportData"
              @generateReport="generateReport"
              @downloadReport="downloadReport"
            ></generate-report>
          </v-card>
        </v-tab-item>
        <v-tab-item :key="3" :value="`tab-3`">
          <v-card flat class="rv-report-content-card">
            <finance-report
              :financeReportData="financeReportData"
              :financeReportError="financeReportError"
              @generateFinanceReport="generateFinanceReport"
            >
            </finance-report>
          </v-card>
        </v-tab-item>
        <v-tab-item :key="4" :value="`tab-4`">
          <v-card flat class="rv-report-content-card">
            <hrdc-event-details
              :isExporting="isExportingEventDetails"
              @exportExcel="exportHrdcEventDetails"
            ></hrdc-event-details>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <HrdcLoader
      :loadingDialog="
        apiGenerateReport.isLoading || apiDownloadReport.isLoading
      "
    ></HrdcLoader>
  </v-container>
</template>
<script>
import * as XLSX from "xlsx";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import FinanceReport from "./FinanceReport";
import GenerateReport from "./GenerateReport";
import HrdcData from "@/services/hrdc_event/objects/globalData";
import DurationAnalysisReport from "./DurationAnalysisReport"; // Hidden — resource heavy
import HrdcEventDetails from "./HrdcEventDetails";
import beforeEnterRoleCheck from "@/services/hrdc/objects/beforeEnterRoleCheck";

export default {
  mixins: [beforeEnterRoleCheck],
  components: {
    DurationAnalysisReport, // Hidden — resource heavy
    GenerateReport,
    FinanceReport,
    HrdcEventDetails,
  },
  computed: mapState({
    auth: (state) => state.auth.data,

  }),
  props: ["params"],
  data: () => ({
    api: new Api(),
    apiGenerateReport: new Api(),
    apiDownloadReport: new Api(),
    apiDownloadFinanceReport: new Api(),
    apiExportApplications: new Api(),
    isExportingEventDetails: false,
    conditions: [],
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    listAllViews: [],
    columns: [],
    modelKey: "",
    parentId: "",
    cColumnSort: [],
    displayRowCount: 10,
    selectedCustomColums: [],
    selectedAdvanceFilterConditions: [],
    selectedFilterConditions: [],
    switchToAdvanceFilter: false,
    isShow: false,
    allResizeColumns: [],
    columnSelectionDisabledCount: 2,
    currentPageNo: 1,
    tab: "tab-2",
    loadingDialog: false,
    generateReportData: {
      searchRptDateRange: [],
      week: null,
      generalInfo: {
        newApplications: null,
        resendApplications: null,
        grantsApproved: null,
        claimsSubmitted: null,
        claimsApproved: null,
        transFrHrdcToYyc: null,
      },
    },
    generateReportError: {
      dateRange: null,
    },
    showGenerateReport: false,
    notReadyReportData: ["pendingGrantsWithIssue"],
    financeReportData: {
      searchRptDateRange: [],
      cutOffBy: null,
    },
    financeReportError: {
      dateRange: null,
      cutOffBy: null,
    },
    financeReportHeaders: [
      "Company Name",
      "Event Name",
      "Price",
      "Invoice No.",
      "Payment Received Date",
    ],
  }),
  async created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.modelKey = this.$route.params.modelKey;
    this.parentId = this.$route.params.parentId;
    this.columns = this.$_.filter(
      this.model.browse.table.headers,
      (headers) => {
        return headers;
      }
    );

    this.stages = await this.hrdcData.allStages;
    this.rolesAssignation(this.auth.roles);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGenerateReport: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/report/generate-report`,
        method: "post",
      },
      apiDownloadReport: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/report/download-report`,
        method: "post",
      },
      apiDownloadFinanceReport: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/report/download-finance-report`,
        method: "post",
      },
      apiExportApplications: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/export-all-applications-excel`,
        method: "get",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    // Perform first check on user role, before enter (to prevent salesperson enter to unwanted view).
    this.checkRoles(this.auth.roles);
  },
  methods: {
    clearErrorMsg() {
      for (let error in this.financeReportError) {
        this.financeReportError[error] = null;
      }
    },
    updateEditedCellData(cell) {
      let editedRecordField = cell.getField();
      let editedRecordValue = cell.getValue();
      let rowData = cell.getData();
      const { applicationUuid, stageId } = rowData;
      const stagePriorNeedToBeExceed = this.hrdcData.stageIndex[stageId];

      let initialValue = null;
      let editMode = false;
      let originalInitialValue = cell.getInitialValue();
      if (originalInitialValue) {
        initialValue = this.$moment(new Date(originalInitialValue)).format(
          "YYYY-MM-DD"
        );
        editMode = true;
      }

      let columnDataType = cell.getColumn().getDefinition().dataType;

      if (columnDataType == "date") {
        if (editedRecordValue) {
          editedRecordValue = new Date(editedRecordValue);
        }
      }

      if (columnDataType == "dateWithBgColor") {
        if (editedRecordValue) {
          editedRecordValue = new Date(editedRecordValue);
        }
      }

      // Set api url based on action.
      const data = this.getUpdateStageData(editedRecordField);

      // Check is this taxpod admin (only taxpod admin allow to edit dates).
      if (!this.isTaxpodAdminRoles) {
        this.$store.dispatch("showMessage", {
          message: "Only taxPOD Admin allow to edit.",
          messageType: "error",
          timeout: 2000,
        });
        cell.setValue(null, true);
        return;
      }

      // Check stage priority.
      if (
        data.stageToExceed > stagePriorNeedToBeExceed ||
        stagePriorNeedToBeExceed == undefined
      ) {
        this.$store.dispatch("showMessage", {
          message: "Please complete previous step to update this date",
          messageType: "warning",
          timeout: 2000,
        });
        cell.setValue(null, true);
        return;
      }

      // Only update if value is not empty.
      if (editedRecordValue) {
        this.api.setMethod("POST");
        let params = {
          applicationUuid,
          referById: this.auth.uuid,
          editMode,
        };

        params[data.dateTerm] = editedRecordValue;

        this.api.setParams(params);
        this.api.setUrl(
          `${this.$service[this.service.key]}/v1/en/console/${data.url}`
        );

        this.api.setCallbackCompleted(() => {
          this.$store.dispatch("showMessage", {
            message: "Updated successfully.",
            messageType: "success",
            timeout: 2000,
          });

          // Set new stageId.
          cell.getRow().getData().stageId = data.nextStageId;

          // Set refund email updated date +14 days.
          if (editedRecordField == "refundEmailArToAp") {
            cell
              .getRow()
              .getCell("refundEmailUpdated")
              .setValue(
                editedRecordValue.setDate(editedRecordValue.getDate() + 14)
              );
          }
        });

        this.api.fetch();
      } else {
        cell.setValue(initialValue, true);
      }
    },
    generateReport(reportData) {
      this.apiGenerateReport.setParams({
        dateRange: reportData.searchRptDateRange,
      });

      this.apiGenerateReport.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (!status) {
            Object.keys(data).forEach((key) => {
              // Dynamically assign error messages based on object keys
              this.generateReportError[key] = data[key];
            });
          }

          if (status) {
            this.generateReportData = {
              searchRptDateRange: reportData.searchRptDateRange,
              week: data.week.toUpperCase(),
              weekStartDate: data.weekStartDate,
              weekEndDate: data.weekEndDate,
              generalInfo: {
                newApplications: data.newApplications,
                resendApplications: data.resendApplications,
                grantsApproved: data.grantsApproved,
                claimsSubmitted: data.claimsSubmitted,
                claimsApproved: data.claimsApproved,
                transFrHrdcToYyc: null,
              },
              ongoingApplications: {
                pendingGrantsApplicationOrApproval:
                  data.pendingGrantsApplicationOrApproval,
                grantsApprovedPendingForm: data.grantsApprovedPendingForm,
                pendingGrantsWithIssue: data.pendingGrantsWithIssue, // TODO
                pendingSubscriptionEndToReqInvoice:
                  data.pendingSubscriptionEndToReqInvoice,
                pendingReqOfHrdcInvoice: 0,
                pendingHrdcInvoiceToSubmitClaim:
                  data.pendingHrdcInvoiceToSubmitClaim,
                pendingSubscriptionEndToSubmitClaim:
                  data.pendingSubscriptionEndToSubmitClaim,
                pendingClaimSubmission: data.pendingClaimSubmission,
                claimsSubmitted: data.onGoingClaimsSubmitted,
                claimsApproved: data.onGoingClaimsApproved,
              },
              pendingRefunds: {
                pendingCnReq: data.pendingRefundCnReq,
                pendingCnApproval: data.pendingGenerateCn,
                pendingEmailSubmissionForRefund:
                  data.pendingEmailSubmissionForRefund,
                pendingEmailApprovalForRefund:
                  data.pendingEmailApprovalForRefund,
                pendingRefund: data.pendingRefundFinance,
                failedRefund: data.pendingRefundFailed,
                pendingEmailPAtoClient: data.pendingRefundEmailPAtoClient,
              },
              pendingAccountEnrollment: data.pendingAccountEnroll,
            };

            this.showGenerateReport = true;
          }
        } catch (err) {
          console.log(err);
        }
      });

      this.apiGenerateReport.fetch();
    },
    async downloadReport(reportData, sumData) {
      await this.$axios
        .get(this.apiDownloadReport.url, {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.data.token}`,
          },
          params: {
            reportData,
            sumData,
          },
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Report.docx");
          document.body.appendChild(link);
          link.click();
        })
        .catch((error) => {
          if ((error.response.status == 404) | true) {
            this.$store.dispatch("showMessage", {
              message:
                "Failed to Preview/Download attachment, kindly contact administrator",
              messageType: "error",
              timeout: 2000,
            });
          }
        });
    },
    // Stream the full event-applications Excel export and trigger a browser
    // download. The endpoint generates the file server-side, so this is a single
    // authed GET returning the .xlsx as a blob.
    async exportHrdcEventDetails() {
      if (this.isExportingEventDetails) return;
      this.isExportingEventDetails = true;

      await this.$axios
        .get(this.apiExportApplications.url, {
          responseType: "blob",
          timeout: 0, // large export — do not let the client time out
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.data.token}`,
          },
        })
        .then((response) => {
          // Prefer the filename the server set; fall back to a stamped name.
          let fileName = `HRDC_Event_Applications_${this.$moment().format(
            "YYYYMMDD_HHmmss"
          )}.xlsx`;
          const disposition = response.headers["content-disposition"];
          if (disposition) {
            const match = disposition.match(
              /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
            );
            if (match && match[1]) {
              fileName = decodeURIComponent(match[1]);
            }
          }

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.log(error);
          this.$store.dispatch("showMessage", {
            message:
              "Failed to export HRDC event details, kindly contact administrator",
            messageType: "error",
            timeout: 2500,
          });
        })
        .finally(() => {
          this.isExportingEventDetails = false;
        });
    },
    async generateFinanceReport(financeReportData) {
      this.loadingDialog = !this.loadingDialog;

      this.apiDownloadFinanceReport.setParams({
        cutOffBy: financeReportData.cutOffBy,
        dateRange: financeReportData.searchRptDateRange,
      });
      this.apiDownloadFinanceReport.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();

          const { status, data } = response;
          if (status == "NO_DATA_FOUND") {
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "info",
              timeout: 2000,
            });
          }

          if (!status) {
            Object.keys(data).forEach((key) => {
              // Dynamically assign error messages based on object keys
              this.financeReportError[key] = data[key];
            });
          }

          if (status === true) {
            let worksheetData = [];
            for (const item of data) {
              worksheetData.push({
                companyName: item.oldCompanyName,
                eventName: item.eventName,
                price: "RM " + item.totalPrice.toLocaleString(),
                hrdcInvoiceNo: item.manualInvoiceToHrdc,
                paymentReceivedDate: item.paymentReceivedDate
                  ? this.$moment(item.paymentReceivedDate).format("YYYY-MM-DD")
                  : null,
              });
            }

            /* generate worksheet and workbook */
            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
              workbook,
              worksheet,
              "Finance Claim Basis"
            );

            /* fix headers */
            XLSX.utils.sheet_add_aoa(worksheet, [this.financeReportHeaders], {
              origin: "A1",
            });

            /* calculate column width */
            var wscols = [
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
            ];

            worksheet["!cols"] = wscols;

            XLSX.writeFile(workbook, `Finance Claim Basis.xlsx`);
          }

          this.loadingDialog = !this.loadingDialog;
        } catch (err) {
          console.log(err);
        }
      });

      this.apiDownloadFinanceReport.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-report-page {
  background: var(--app-page);
}

.dark-mode .rv-report-header {
  background: var(--app-elevated);
}

.dark-mode .rv-report-back-btn {
  border: 1px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .rv-report-back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .rv-report-tabs-wrapper {
  background: var(--app-elevated);
}

.dark-mode .rv-report-tabs-wrapper >>> .v-tab {
  color: var(--app-text-muted) !important;
}

.dark-mode .rv-report-tabs-wrapper >>> .v-tab:hover {
  color: var(--app-text) !important;
  background: var(--app-hover) !important;
}

.dark-mode .rv-report-tabs-wrapper >>> .v-tab--active {
  background: var(--app-card) !important;
}

.dark-mode .rv-report-content {
  background: var(--app-card);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE LAYOUT
   ============================================ */
.rv-report-page {
  background: #d8dce2;
  font-family: 'Lato', sans-serif;
  min-height: 100vh;
  padding: 24px 28px !important;
}

/* ============================================
   HEADER
   ============================================ */
.rv-report-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 20px;
  overflow: hidden;
}

.rv-report-header__top {
  display: flex;
  align-items: center;
  padding: 22px 28px;
}

.rv-report-back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.rv-report-back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.rv-report-header__info {
  flex: 1;
  min-width: 0;
}

.rv-report-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  letter-spacing: -0.2px;
}

/* Title kept its light near-black and disappeared into the elevated header
   surface in dark mode. */
.dark-mode .rv-report-header__title {
  color: var(--app-text);
}

/* ============================================
   TABS
   ============================================ */
.rv-report-tabs-wrapper {
  background: #eaecef;
  border-radius: 14px 14px 0 0;
  padding: 6px 16px 0;
}

.rv-report-tabs-wrapper >>> .v-tabs-slider-wrapper {
  color: #0ea5e9 !important;
}

.rv-report-tabs-wrapper >>> .v-tab {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: #64748b !important;
  min-width: auto !important;
  padding: 0 18px !important;
  border-radius: 10px 10px 0 0;
  transition: all 0.2s ease;
}

.rv-report-tabs-wrapper >>> .v-tab:hover {
  color: #334155 !important;
  background: rgba(15, 23, 42, 0.08) !important;
}

.rv-report-tabs-wrapper >>> .v-tab--active {
  color: #0ea5e9 !important;
  background: #fff !important;
}

.rv-report-tabs-wrapper >>> .v-tab--active .v-icon {
  color: #0ea5e9 !important;
}

/* ============================================
   CONTENT
   ============================================ */
.rv-report-content {
  background: #fff;
  border-radius: 0 0 14px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.rv-report-content-card {
  background: transparent !important;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .rv-report-page {
    padding: 12px 10px !important;
  }

  .rv-report-header__top {
    padding: 16px 18px;
  }

  .rv-report-header__title {
    font-size: 1rem;
  }

  .rv-report-tabs-wrapper >>> .v-tab {
    font-size: 0.72rem !important;
    padding: 0 10px !important;
  }
}

@media (max-width: 480px) {
  .rv-report-tabs-wrapper >>> .v-tab {
    font-size: 0.68rem !important;
    padding: 0 8px !important;
  }
}
</style>
